import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "elements/Box";
import { Button, IconButton, Pagination, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from "@mui/material";
import TuneIcon from '@mui/icons-material/Tune';
import Filters2 from 'elements/Filters/Filters2';
import FiltersApplied2 from 'elements/Filters/FiltersApplied2';
import { PiCheckCircleFill, PiCircle, PiCheckCircleBold, PiCircleBold } from 'react-icons/pi';
import PickingVerifyDialog from './PickingVerifyDialog';

import "assets/css/main.css";
import { ApiRequest } from "GlobalFunctions";

/*=========================================================
PICKING STATUS BAR
- Shows 3 phases: Bodega (red), Certificación (orange), Conductor (green)
- guide_status: 1=Bodega, 2=Certificación, 3=Conductor, 4=Finalizado
- Phase is "filled" when guide_status > phase_key
- Phase is "active" when guide_status === phase_key
=========================================================*/
const ALL_PHASES = [
  { key: 1, label: 'Bodega', color: '#E53935' },
  { key: 2, label: 'Certificación', color: '#FB8C00' },
  { key: 3, label: 'Conductor', color: '#43A047' },
];

function PickingStatusBar({ guideStatus, pickingCertification = true }: { guideStatus: number; pickingCertification?: boolean }) {
  const PHASES = pickingCertification ? ALL_PHASES : ALL_PHASES.filter(p => p.key !== 2);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
      {PHASES.map((phase) => {
        const isCompleted = guideStatus > phase.key;
        const isActive = guideStatus === phase.key;
        const color = isCompleted || isActive ? phase.color : '#bdbdbd';

        return (
          <Tooltip
            key={phase.key}
            title={isCompleted ? `${phase.label}: Completado` : isActive ? `${phase.label}: En progreso` : `${phase.label}: Pendiente`}
            arrow
            placement="top"
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'default' }}>
              {isCompleted
                ? <PiCheckCircleFill size={18} color={phase.color} />
                : <PiCircle size={18} color={color} />
              }
              <span style={{ fontSize: '12px', fontWeight: isActive ? 700 : 400, color }}>
                {phase.label}
              </span>
            </div>
          </Tooltip>
        );
      })}
    </div>
  );
}

/*=========================================================
PICKING LIST
=========================================================*/
export default function PickingList(props: any) {
  const { setLoading } = props;
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(false);
  }, []);

  const [list, setList] = useState<any[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [filtersValues, setFiltersValues] = useState<any>([]);
  const [open, setOpen] = useState(false);
  const [pickingCertification, setPickingCertification] = useState(true);

  // Verify dialog state
  const [openVerifyDialog, setOpenVerifyDialog] = useState(false);
  const [verifyGuideCode, setVerifyGuideCode] = useState<string | null>(null);
  const [verifyCurrentVerified, setVerifyCurrentVerified] = useState<number>(0);

  useEffect(() => {
    setLoading(true);
    loadList();
  }, [page, filtersValues]);

  const loadList = async () => {
    setLoading(true);

    const query: any = { page };

    if (filtersValues.length > 0) {
      filtersValues.forEach((f: any) => {
        if (f.value) {
          query[f.name] = f.value;
        }
      });
    }

    await ApiRequest({
      url: '/picking/listGuidesWeb',
      method: 'GET',
      query,
      headers: {
        ltkn: localStorage.getItem("ltkn"),
      },
      setResponse: (response: any) => {
        if (response.status === 'success') {
          setList(response.list || []);
          setTotalPages(response.total_pages || 0);
          if (response.picking_certification !== undefined) {
            setPickingCertification(response.picking_certification);
          }
        }
        setLoading(false);
      }
    });
  };

  const filters = [
    { type: "text", label: "Código de Guía", name: "guide_code", value: "" },
    { type: "select", label: "Estado", name: "status", value: "", options: [
      { label: "Todos", value: "" },
      { label: "Bodega", value: "1" },
      ...(pickingCertification ? [{ label: "Certificación", value: "2" }] : []),
      { label: "Conductor", value: "3" },
      { label: "Finalizado", value: "4" },
    ]},
  ];

  const setFilters = (tempFilters: any) => {
    setFiltersValues(tempFilters);
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusLabel = (status: number) => {
    switch (status) {
      case 1: return 'Bodega';
      case 2: return 'Certificación';
      case 3: return 'Conductor';
      case 4: return 'Finalizado';
      default: return '-';
    }
  };

  return (
    <div id="PickingList" className="ModuleContainer">
      <div className="ModuleWrapper">
        <div className="boxContainerHeader">
          <div className="boxContainerTitle">
            <h2 className="h2_title">Picking</h2>
            <span className="h2_subtitle">Guías de picking</span>
          </div>
          <div className='module_tools'>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<TuneIcon />}
              onClick={() => setOpen(true)}
            >
              Filtrar
            </Button>
          </div>
        </div>

        <Filters2 open={open} setOpen={setOpen} filters={filters} setFilters={setFilters} />
        <FiltersApplied2 filtersValues={filtersValues} setFiltersValues={setFiltersValues} filters={filters} />

        <Box id="PickingListIn" className="box100">
          <TableContainer>
            <Table aria-label="picking guides table">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: "#a8a8a8", borderBottom: "none" }}># De Guía</TableCell>
                  <TableCell sx={{ color: "#a8a8a8", borderBottom: "none" }}>Productos</TableCell>
                  <TableCell sx={{ color: "#a8a8a8", borderBottom: "none" }}>Estado</TableCell>
                  <TableCell sx={{ color: "#a8a8a8", borderBottom: "none" }}>Creación</TableCell>
                  <TableCell sx={{ color: "#a8a8a8", borderBottom: "none" }}>Última actualización</TableCell>
                  <TableCell sx={{ color: "#a8a8a8", borderBottom: "none", textAlign: "center" }}>Verificado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {list.map((item, i) => (
                  <TableRow 
                    key={i} 
                    hover 
                    role="checkbox"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/picking/details/${item.guide_code}`)}
                  >
                    <TableCell>
                      <span className='flex_title'>{item.guide_code}</span>
                    </TableCell>
                    <TableCell>
                      <span className='flex_title'>{item.total_products} productos</span>
                    </TableCell>
                    <TableCell>
                      <PickingStatusBar guideStatus={item.guide_status} pickingCertification={pickingCertification} />
                    </TableCell>
                    <TableCell>
                      <span className='flex_subtitle'>{formatDate(item.created_at)}</span>
                    </TableCell>
                    <TableCell>
                      <span className='flex_subtitle'>{formatDate(item.updated_at)}</span>
                    </TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      {item.verified === 1 ? (
                        <Tooltip title="Verificado" arrow>
                          <span
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              setVerifyGuideCode(item.guide_code);
                              setVerifyCurrentVerified(item.verified || 0);
                              setOpenVerifyDialog(true);
                            }}
                          >
                            <PiCheckCircleBold style={{ color: '#4caf50', fontSize: '1.5rem' }} />
                          </span>
                        </Tooltip>
                      ) : (
                        <Tooltip title="No verificado" arrow>
                          <span
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              setVerifyGuideCode(item.guide_code);
                              setVerifyCurrentVerified(item.verified || 0);
                              setOpenVerifyDialog(true);
                            }}
                          >
                            <PiCircleBold style={{ color: '#bdbdbd', fontSize: '1.5rem' }} />
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(event, value) => setPage(value)}
            color="primary"
            style={{ marginTop: "20px", alignSelf: "center" }}
          />
        </Box>
      </div>

      <PickingVerifyDialog
        setLoading={setLoading}
        openDialog={openVerifyDialog}
        setOpenDialog={setOpenVerifyDialog}
        loadList={loadList}
        guideCode={verifyGuideCode}
        currentVerified={verifyCurrentVerified}
      />
    </div>
  );
}
